/**
 * Media library.
 *
 * Alt text is required for images before upload is allowed. That is a deliberate friction: an
 * accessible site is impossible if the library fills up with undescribed images, and asking at
 * upload time is the only point where the person knows what the image shows.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, Badge, Button, Card, FormField, Input, Modal, Select, Skeleton, Textarea } from '@/components/ui';
import { Icon, LoadError } from '@/components/site/blocks';
import { useAuth } from '@/context/AuthContext';
import { useDebounced } from '@/hooks';
import { cmsApi } from '@/services/api';
import { ApiError } from '@/services/client';
import type { MediaAsset } from '@/services/types';

const KIND_ICONS: Record<string, string> = { image: 'scan', video: 'video', document: 'file-text', audio: 'activity' };

function UploadDialog({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [altText, setAltText] = useState('');
  const [caption, setCaption] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isImage = file ? /^image\//.test(file.type) : false;

  const mutation = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('file', file!);
      if (title) form.append('title', title);
      if (altText) form.append('alt_text', altText);
      if (caption) form.append('caption', caption);
      return cmsApi.uploadMedia(form);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cms', 'media'] });
      onClose();
    },
    onError: (caught) => {
      setErrors(caught instanceof ApiError ? caught.fields : { file: 'The upload failed.' });
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title="Upload a file"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!file || (isImage && !altText.trim())}
            loading={mutation.isPending}
          >
            Upload
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {errors.file && <Alert tone="danger">{errors.file}</Alert>}

        <FormField label="File" htmlFor="upload-file" required {...(errors.file ? { error: errors.file } : {})}>
          <input
            id="upload-file"
            type="file"
            accept=".jpg,.jpeg,.png,.webp,.avif,.svg,.gif,.mp4,.webm,.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.csv"
            onChange={(event) => {
              const selected = event.target.files?.[0] ?? null;
              setFile(selected);
              if (selected && !title) setTitle(selected.name.replace(/\.[^.]+$/, '').replace(/[-_]/g, ' '));
            }}
            className="w-full cursor-pointer rounded-md border border-dashed border-line-strong bg-raised px-3.5 py-3 text-sm file:mr-3 file:rounded file:border-0 file:bg-primary file:px-3 file:py-1.5 file:text-sm file:font-semibold file:text-primary-contrast"
          />
          <p className="mt-1 text-xs text-muted">
            Images, video, PDFs and Office documents. Files are validated on extension, type and content.
          </p>
        </FormField>

        <FormField label="Title" htmlFor="upload-title">
          <Input id="upload-title" value={title} onChange={(event) => setTitle(event.target.value)} />
        </FormField>

        <FormField
          label="Alternative text"
          htmlFor="upload-alt"
          required={isImage}
          hint={
            isImage
              ? 'Describe what the image shows. Required for images so the site stays usable with a screen reader.'
              : 'Optional for non-image files.'
          }
          {...(errors.alt_text ? { error: errors.alt_text } : {})}
        >
          <Input
            id="upload-alt"
            value={altText}
            onChange={(event) => setAltText(event.target.value)}
            error={Boolean(errors.alt_text)}
          />
        </FormField>

        <FormField label="Caption" htmlFor="upload-caption">
          <Textarea id="upload-caption" rows={2} value={caption} onChange={(event) => setCaption(event.target.value)} />
        </FormField>
      </div>
    </Modal>
  );
}

function AssetDetail({ asset, onClose }: { asset: MediaAsset; onClose: () => void }) {
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const [fields, setFields] = useState({
    title: asset.title,
    alt_text: asset.alt_text,
    caption: asset.caption,
    credit: asset.credit,
    tags: asset.tags,
  });

  const saveMutation = useMutation({
    mutationFn: () => cmsApi.updateMedia(asset.id, fields),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cms', 'media'] });
      onClose();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => cmsApi.deleteMedia(asset.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['cms', 'media'] });
      onClose();
    },
  });

  return (
    <Modal
      open
      onClose={onClose}
      title={asset.title || asset.original_filename}
      size="lg"
      footer={
        <>
          {can('can_manage_media') && (
            <Button
              variant="danger"
              onClick={() => {
                if (window.confirm('Delete this asset? Any content using it will lose the image.')) deleteMutation.mutate();
              }}
              loading={deleteMutation.isPending}
              className="mr-auto"
            >
              Delete
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending}>
            Save
          </Button>
        </>
      }
    >
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          {asset.kind === 'image' ? (
            <img src={asset.url} alt={asset.alt_text} className="w-full rounded-lg border border-line" />
          ) : (
            <div className="flex aspect-video items-center justify-center rounded-lg bg-raised">
              <Icon name={KIND_ICONS[asset.kind] ?? 'file-text'} className="h-10 w-10 text-muted" />
            </div>
          )}
          <dl className="mt-4 space-y-1.5 text-xs">
            {[
              { label: 'Filename', value: asset.original_filename },
              { label: 'Type', value: asset.mime_type },
              { label: 'Size', value: asset.size_display },
              ...(asset.width ? [{ label: 'Dimensions', value: `${asset.width} × ${asset.height}` }] : []),
              { label: 'Uploaded by', value: asset.uploaded_by || 'Unknown' },
              { label: 'Renditions', value: Object.keys(asset.variants ?? {}).join(', ') || 'None generated' },
            ].map((item) => (
              <div key={item.label} className="flex gap-2">
                <dt className="text-muted">{item.label}:</dt>
                <dd className="min-w-0 flex-1 truncate text-ink">{item.value}</dd>
              </div>
            ))}
          </dl>
        </div>

        <div className="space-y-4">
          <FormField label="Title" htmlFor="asset-title">
            <Input id="asset-title" value={fields.title} onChange={(event) => setFields({ ...fields, title: event.target.value })} />
          </FormField>
          <FormField
            label="Alternative text"
            htmlFor="asset-alt"
            required={asset.kind === 'image'}
            hint="What a screen-reader user would need to know."
          >
            <Input id="asset-alt" value={fields.alt_text} onChange={(event) => setFields({ ...fields, alt_text: event.target.value })} />
          </FormField>
          <FormField label="Caption" htmlFor="asset-caption">
            <Textarea id="asset-caption" rows={2} value={fields.caption} onChange={(event) => setFields({ ...fields, caption: event.target.value })} />
          </FormField>
          <FormField label="Credit" htmlFor="asset-credit" hint="Attribution or licence note, if the asset requires one.">
            <Input id="asset-credit" value={fields.credit} onChange={(event) => setFields({ ...fields, credit: event.target.value })} />
          </FormField>
          <FormField label="Tags" htmlFor="asset-tags" hint="Comma separated, used by library search.">
            <Input id="asset-tags" value={fields.tags} onChange={(event) => setFields({ ...fields, tags: event.target.value })} />
          </FormField>
        </div>
      </div>
    </Modal>
  );
}

export function MediaPage() {
  const [search, setSearch] = useState('');
  const [kind, setKind] = useState('');
  const [page, setPage] = useState(1);
  const [uploading, setUploading] = useState(false);
  const [selected, setSelected] = useState<MediaAsset | null>(null);
  const debouncedSearch = useDebounced(search, 300);

  const listQuery = useQuery({
    queryKey: ['cms', 'media', { debouncedSearch, kind, page }],
    queryFn: () => cmsApi.media({ search: debouncedSearch || undefined, kind: kind || undefined, page, page_size: 36 }),
  });

  const statsQuery = useQuery({ queryKey: ['cms', 'media-stats'], queryFn: cmsApi.mediaStats });

  if (listQuery.isError) return <LoadError onRetry={() => void listQuery.refetch()} />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">Media library</h1>
          <p className="mt-1 text-sm text-muted">
            {statsQuery.data
              ? `${statsQuery.data.total} assets · ${(statsQuery.data.total_bytes / 1024 / 1024).toFixed(1)} MB`
              : 'Images, video and documents used across the website.'}
          </p>
        </div>
        <Button onClick={() => setUploading(true)}>Upload a file</Button>
      </div>

      <div className="flex flex-wrap gap-3">
        <div className="min-w-52 flex-1">
          <label htmlFor="media-search" className="sr-only">
            Search the library
          </label>
          <Input
            id="media-search"
            type="search"
            placeholder="Search by title, alt text, filename or tag…"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
          />
        </div>
        <div>
          <label htmlFor="media-kind" className="sr-only">
            Filter by type
          </label>
          <Select
            id="media-kind"
            value={kind}
            onChange={(event) => {
              setKind(event.target.value);
              setPage(1);
            }}
          >
            <option value="">All types</option>
            <option value="image">Images</option>
            <option value="video">Video</option>
            <option value="document">Documents</option>
            <option value="audio">Audio</option>
          </Select>
        </div>
      </div>

      {listQuery.isLoading ? (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
          {Array.from({ length: 12 }).map((_, index) => (
            <Skeleton key={index} className="aspect-square" />
          ))}
        </div>
      ) : (listQuery.data?.results.length ?? 0) === 0 ? (
        <Card className="py-14 text-center">
          <p className="font-display text-lg font-semibold text-ink">The library is empty</p>
          <p className="mx-auto mt-2 max-w-md text-sm text-muted">
            Upload your logo, product images, architecture diagrams and datasheets. The site shows tasteful placeholders
            until you do.
          </p>
          <Button className="mt-6" onClick={() => setUploading(true)}>
            Upload your first file
          </Button>
        </Card>
      ) : (
        <>
          <p className="text-sm text-muted" role="status">
            {listQuery.data!.count} asset{listQuery.data!.count === 1 ? '' : 's'}
          </p>
          <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
            {listQuery.data!.results.map((asset) => (
              <li key={asset.id}>
                <button
                  type="button"
                  onClick={() => setSelected(asset)}
                  className="group w-full overflow-hidden rounded-lg border border-line bg-surface text-left transition-colors hover:border-primary focus-visible:ring-2 focus-visible:ring-primary"
                >
                  <span className="flex aspect-square items-center justify-center overflow-hidden bg-sunken">
                    {asset.kind === 'image' ? (
                      <img src={asset.url} alt={asset.alt_text} loading="lazy" className="h-full w-full object-cover" />
                    ) : (
                      <Icon name={KIND_ICONS[asset.kind] ?? 'file-text'} className="h-8 w-8 text-muted" />
                    )}
                  </span>
                  <span className="block px-3 py-2.5">
                    <span className="block truncate text-xs font-medium text-ink">{asset.title || asset.original_filename}</span>
                    <span className="mt-0.5 flex items-center gap-1.5">
                      <span className="text-[0.625rem] text-muted">{asset.size_display}</span>
                      {asset.kind === 'image' && !asset.alt_text && <Badge tone="warning">No alt text</Badge>}
                    </span>
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}

      {uploading && <UploadDialog onClose={() => setUploading(false)} />}
      {selected && <AssetDetail asset={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}
